import React, { useEffect, useRef, useLayoutEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Bold, Italic, List, ListOrdered, Link2, Heading } from 'lucide-react';
import { formatMessage } from './RegistrationFormPanel';

// Compact formatted-text box for the registration builder (waivers, welcome and
// confirmation messages): no HTML for the AD to read or type. Stores HTML; plain text
// pasted earlier (e.g. from an email) opens already formatted. Grows with its text.
const isEmpty = (h) => !h || h.replace(/<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>/g, '').trim() === '';

export default function RegRichText({ value, onChange, placeholder, isDark, testid, minHeight = 64 }) {
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [4] } }), Link.configure({ openOnClick: false, autolink: true })],
    content: formatMessage(value || ''),
    editorProps: {
      attributes: {
        class: 'outline-none text-sm leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_h4]:font-semibold [&_p]:my-1 [&_a]:underline',
        style: `min-height:${minHeight}px`,
        'data-testid': testid || '',
      },
    },
    onUpdate: ({ editor: ed }) => { const h = ed.getHTML(); onChange(isEmpty(h) ? '' : h); },
  });
  // Follow outside changes (e.g. a template applied) without fighting the cursor.
  useEffect(() => {
    if (!editor) return;
    const want = formatMessage(value || '');
    if (!editor.isFocused && want !== editor.getHTML() && !(isEmpty(want) && isEmpty(editor.getHTML()))) {
      editor.commands.setContent(want, { emitUpdate: false });
    }
  }, [value, editor]);

  const btn = (on, onClick, Icon, title) => (
    <button type="button" title={title} aria-label={title} onMouseDown={e => e.preventDefault()} onClick={onClick}
      className={`p-1 rounded ${on ? 'bg-amber-600 text-white' : isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}>
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
  const link = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Link address (leave empty to remove)', prev);
    if (url === null) return;
    if (!url) editor.chain().focus().unsetLink().run();
    else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };
  return (
    <div className={`rounded border ${isDark ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
      {editor && (
        <div className={`flex items-center gap-0.5 px-1.5 py-1 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), Bold, 'Bold')}
          {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), Italic, 'Italic')}
          {btn(editor.isActive('heading'), () => editor.chain().focus().toggleHeading({ level: 4 }).run(), Heading, 'Heading')}
          {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), List, 'Bullet list')}
          {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, 'Numbered list')}
          {btn(editor.isActive('link'), link, Link2, 'Link')}
          {placeholder && isEmpty(editor.getHTML()) && <span className="ml-2 text-xs text-slate-400 truncate">{placeholder}</span>}
        </div>
      )}
      <div className="px-2.5 py-1.5"><EditorContent editor={editor} /></div>
    </div>
  );
}

// A one-line-looking text box that grows to show all of its text (no manual resizing).
export function AutoText({ value, onChange, className = '', singleLine = false, ...rest }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current; if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);
  return (
    <textarea ref={ref} rows={1} value={value} onChange={onChange}
      onKeyDown={singleLine ? (e => { if (e.key === 'Enter') e.preventDefault(); }) : undefined}
      className={`${className} resize-none overflow-hidden leading-snug py-1.5`} {...rest} />
  );
}
